import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import {
  groupMeOnlyEventDisplayDate,
  summarizeGroupMeOnlyRsvps,
  type GroupMeOnlyRsvpStatus,
} from '../utils/groupmeOnlyRsvp';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';

const productionWebhookUrl = 'https://app.courtcaptain.net/api/groupme/webhook';
const stagingWebhookUrl = 'https://staging.courtcaptain.net/api/groupme/webhook';

type SyncStatus = 'logged' | 'ignored' | 'matched' | 'synced' | 'needs_review' | 'error';
type PageTab = 'configuration' | 'setup' | 'mappings' | 'structured' | 'diagnostics' | 'review' | 'groupme-only' | 'events';
type GroupMeSpikeViewState = {
  pageTab: PageTab;
  selectedTeamId: string;
};

type TeamRow = {
  id: string;
  team_name: string;
  groupme_group_id: string | null;
  groupme_group_name: string | null;
};

type PlayerRow = {
  id: string;
  first_name: string;
  last_name: string;
  groupme_display_name: string | null;
};

type GroupMeMemberOption = {
  userId: string;
  membershipId: string | null;
  name: string | null;
  nickname: string | null;
  displayName: string;
  mappedPlayerId: string | null;
  mappedPlayerName: string | null;
};

type RosterRow = { team_id: string; player_id: string };

type MappedEvent = {
  id: string;
  kind: 'match' | 'event';
  team_id: string;
  label: string;
  date: string;
  groupme_event_id: string | null;
  groupme_event_title: string | null;
};

type GroupMeWebhookEvent = {
  id: string;
  received_at: string;
  source: string;
  groupme_message_id: string | null;
  groupme_group_id: string | null;
  groupme_event_id: string | null;
  sender_name: string | null;
  sender_id: string | null;
  system: boolean;
  text: string | null;
  raw_payload: unknown;
  parsed_event_title: string | null;
  parsed_player_name: string | null;
  parsed_status: 'available' | 'unavailable' | 'tentative' | null;
  parse_status: 'unparsed' | 'parsed' | 'ignored' | 'error';
  matched_team_id: string | null;
  matched_event_id: string | null;
  matched_event_kind: 'match' | 'event' | null;
  matched_player_id: string | null;
  sync_status: SyncStatus;
  sync_error: string | null;
};

type FeatureStatus = {
  rsvpSyncEnabled: boolean;
  rsvpSyncConfigured: boolean;
  webhookEnvEnabled?: boolean;
};

type UserGroupMeTokenStatus = {
  tokenConfigured: boolean;
  tokenNeedsResave?: boolean;
  serverConfigured: boolean;
  missingConfigKeys?: string[];
  lastTestedAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  fallbackAvailable: boolean;
};

type ClassificationRule = {
  matchType: 'begins_with' | 'contains';
  cues: string[];
  classification: 'Match' | 'Practice' | 'Social Event' | 'Meeting' | 'Other';
};

type GroupMeAdminSettings = {
  structuredApiEnabled: boolean;
  webhookApplyEnabled: boolean;
  webhookEnvEnabled: boolean;
  classificationRules: ClassificationRule[];
  updatedAt: string | null;
};

type GroupMeOnlyEvent = {
  id: string;
  workspace_id: string;
  team_id: string | null;
  groupme_group_id: string;
  groupme_group_name: string | null;
  groupme_event_id: string | null;
  event_name: string;
  event_start_at: string | null;
  event_end_at: string | null;
  event_status: string;
  last_synced_at: string | null;
  source_updated_at: string | null;
  sync_status: string;
  sync_error: string | null;
};

type GroupMeOnlyParticipant = {
  id: string;
  groupme_only_rsvp_event_id: string;
  groupme_user_id: string | null;
  groupme_display_name: string;
  latest_rsvp_status: GroupMeOnlyRsvpStatus;
  source_updated_at: string | null;
  matched_player_id: string | null;
  match_status: 'matched' | 'ambiguous' | 'unmatched';
  match_confidence: number | null;
  mapping_source: string;
  players?: {
    id: string;
    first_name: string;
    last_name: string;
    sex: string | null;
  } | null;
};

type StructuredGroupMeSyncRow = {
  id: string;
  team_id: string;
  groupme_group_id: string;
  groupme_event_id: string;
  court_captain_event_id: string | null;
  court_captain_event_type: 'match' | 'event' | null;
  raw_name: string | null;
  raw_start_at: string | null;
  raw_end_at: string | null;
  raw_timezone: string | null;
  classification_status: string;
  classification_reason: string | null;
  parsed_opponent_name: string | null;
  parsed_match_site_type: string | null;
  sync_status: string;
  sync_error: string | null;
  mapped_count: number;
  ambiguous_count: number;
  unmatched_count: number;
  going_count: number;
  maybe_count: number;
  not_going_count: number;
  no_response_count: number;
  last_synced_at: string | null;
  source_updated_at: string | null;
};

type GroupMeEventSnapshot = {
  id: string;
  team_id: string;
  groupme_group_id: string;
  groupme_event_id: string;
  captured_at: string;
  raw_payload?: unknown;
  normalized_subset: Record<string, unknown>;
  event_content_hash: string;
  rsvp_hash: string;
  notes?: string | null;
};

type GroupMeEventDiagnosticPayload = {
  rawPayload?: unknown;
  normalizedSubset?: Record<string, unknown>;
  eventContentHash?: string;
  rsvpHash?: string;
  snapshots?: GroupMeEventSnapshot[];
  snapshot?: GroupMeEventSnapshot;
  first?: GroupMeEventSnapshot;
  second?: GroupMeEventSnapshot;
  changed?: { eventContentHash: boolean; rsvpHash: boolean };
  diff?: Array<Record<string, unknown>>;
  normalizedDiff?: Array<Record<string, unknown>>;
  error?: string;
};

type Snack = { severity: 'success' | 'warning' | 'error'; message: string };

const defaultClassificationRules: ClassificationRule[] = [
  { matchType: 'begins_with', cues: ['vs', '@', 'VS', 'Vs'], classification: 'Match' },
  { matchType: 'contains', cues: ['happy hour', 'social', 'party'], classification: 'Social Event' },
  { matchType: 'contains', cues: ['practice', 'drill'], classification: 'Practice' },
];

function normalizeName(value: string | null | undefined): string {
  return (value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('en-US');
}

function statusColor(status: SyncStatus): 'success' | 'warning' | 'error' | 'default' | 'info' {
  if (status === 'synced') return 'success';
  if (status === 'needs_review') return 'warning';
  if (status === 'error') return 'error';
  if (status === 'matched') return 'info';
  return 'default';
}

function formattedTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function GroupMeSpike({ isAdmin }: { isAdmin: boolean }) {
  const auditUserId = useAuditUserId();
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState<GroupMeSpikeViewState>({
    userId: auditUserId,
    routeKey: 'groupme-spike',
    defaultState: { pageTab: 'setup', selectedTeamId: '' },
  });
  const [pageTab, setPageTab] = useState<PageTab>(savedViewState.pageTab);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [rosters, setRosters] = useState<RosterRow[]>([]);
  const [mappedEvents, setMappedEvents] = useState<MappedEvent[]>([]);
  const [events, setEvents] = useState<GroupMeWebhookEvent[]>([]);
  const [groupMeOnlyEvents, setGroupMeOnlyEvents] = useState<GroupMeOnlyEvent[]>([]);
  const [groupMeOnlyParticipants, setGroupMeOnlyParticipants] = useState<GroupMeOnlyParticipant[]>([]);
  const [structuredSyncRows, setStructuredSyncRows] = useState<StructuredGroupMeSyncRow[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState(savedViewState.selectedTeamId);
  const [reviewPlayerSelections, setReviewPlayerSelections] = useState<Record<string, string>>({});
  const [reviewEventSelections, setReviewEventSelections] = useState<Record<string, string>>({});
  const [participantPlayerSelections, setParticipantPlayerSelections] = useState<Record<string, string>>({});
  const [featureStatus, setFeatureStatus] = useState<FeatureStatus>({ rsvpSyncEnabled: false, rsvpSyncConfigured: false });
  const [userTokenStatus, setUserTokenStatus] = useState<UserGroupMeTokenStatus | null>(null);
  const [userTokenDraft, setUserTokenDraft] = useState('');
  const [adminSettings, setAdminSettings] = useState<GroupMeAdminSettings>({
    structuredApiEnabled: true,
    webhookApplyEnabled: false,
    webhookEnvEnabled: false,
    classificationRules: defaultClassificationRules,
    updatedAt: null,
  });
  const [classificationDrafts, setClassificationDrafts] = useState<Array<ClassificationRule & { cuesText: string }>>(
    defaultClassificationRules.map((rule) => ({ ...rule, cuesText: rule.cues.join(', ') })),
  );
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [groupMeMembers, setGroupMeMembers] = useState<GroupMeMemberOption[]>([]);
  const [groupMeMembersLoading, setGroupMeMembersLoading] = useState(false);
  const [groupMeMembersDiagnostic, setGroupMeMembersDiagnostic] = useState<string | null>(null);
  const [playerMemberSelections, setPlayerMemberSelections] = useState<Record<string, string>>({});
  const [diagnosticEventId, setDiagnosticEventId] = useState('');
  const [diagnosticPayload, setDiagnosticPayload] = useState<GroupMeEventDiagnosticPayload | null>(null);
  const [diagnosticSnapshots, setDiagnosticSnapshots] = useState<GroupMeEventSnapshot[]>([]);
  const [firstSnapshotId, setFirstSnapshotId] = useState('');
  const [secondSnapshotId, setSecondSnapshotId] = useState('');
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'groupme-spike', contextKey: pageTab, ready: !loading });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setPageTab(savedViewState.pageTab);
    setSelectedTeamId(savedViewState.selectedTeamId);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ pageTab, selectedTeamId });
  }, [pageTab, selectedTeamId, setSavedViewState, viewStateKey, viewStateReady]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    const [statusResponse, userStatusResponse, adminSettingsResponse, teamsResult, playersResult, rostersResult, matchesResult, teamEventsResult, webhookResult, groupMeOnlyEventResult, groupMeOnlyParticipantResult, structuredSyncResult] = await Promise.all([
      fetch('/api/groupme/webhook').then((response) => response.json()).catch(() => ({})),
      fetch('/api/groupme/integration/user-status', { headers: authHeader }).then((response) => response.json()).catch(() => ({})),
      fetch('/api/groupme/integration/admin-settings', { headers: authHeader }).then((response) => response.json()).catch(() => ({})),
      supabase.from('teams').select('id, team_name, groupme_group_id, groupme_group_name').eq('is_active', true).is('deleted_at', null).order('team_name'),
      supabase.from('players').select('id, first_name, last_name, groupme_display_name').eq('is_active', true).is('deleted_at', null).order('last_name').order('first_name'),
      supabase.from('team_rosters').select('team_id, player_id').eq('is_active', true).is('deleted_at', null),
      supabase.from('match_events').select('id, team_id, match_date, opponent_name, groupme_event_id, groupme_event_title').is('deleted_at', null).order('match_date', { ascending: false }).limit(200),
      supabase.from('events').select('id, team_id, event_date, title, event_type, groupme_event_id, groupme_event_title').is('deleted_at', null).neq('event_type', 'Match').order('event_date', { ascending: false }).limit(200),
      supabase.from('groupme_webhook_events').select('*').order('received_at', { ascending: false }).limit(100),
      supabase.from('groupme_only_rsvp_events').select('*').order('source_updated_at', { ascending: false }).limit(100),
      supabase.from('groupme_only_rsvp_participants').select('*, players(id, first_name, last_name, sex)').order('source_updated_at', { ascending: false }).limit(500),
      supabase.from('groupme_event_sync').select('*').order('last_synced_at', { ascending: false }).limit(100),
    ]);
    setLoading(false);

    const error = teamsResult.error || playersResult.error || rostersResult.error || matchesResult.error || teamEventsResult.error || webhookResult.error
      || groupMeOnlyEventResult.error || groupMeOnlyParticipantResult.error || structuredSyncResult.error;
    if (error) {
      setSnack({
        severity: 'error',
        message: error.message.includes('groupme_')
          ? 'Unable to load GroupMe RSVP Sync. Apply the latest GroupMe migrations to this environment first.'
          : error.message,
      });
      return;
    }

    const nextTeams = (teamsResult.data || []) as TeamRow[];
    setFeatureStatus({
      rsvpSyncEnabled: adminSettingsResponse.webhookApplyEnabled === true,
      rsvpSyncConfigured: statusResponse.rsvpSyncConfigured === true,
      webhookEnvEnabled: statusResponse.webhookEnvEnabled === true,
    });
    if ('tokenConfigured' in userStatusResponse) setUserTokenStatus(userStatusResponse as UserGroupMeTokenStatus);
    if ('structuredApiEnabled' in adminSettingsResponse) {
      const nextSettings = {
        ...adminSettingsResponse,
        classificationRules: Array.isArray(adminSettingsResponse.classificationRules) && adminSettingsResponse.classificationRules.length
          ? adminSettingsResponse.classificationRules
          : defaultClassificationRules,
      } as GroupMeAdminSettings;
      setAdminSettings(nextSettings);
      setClassificationDrafts(nextSettings.classificationRules.map((rule) => ({ ...rule, cuesText: rule.cues.join(', ') })));
    }
    setTeams(nextTeams);
    setPlayers((playersResult.data || []) as PlayerRow[]);
    setRosters((rostersResult.data || []) as RosterRow[]);
    setEvents((webhookResult.data || []) as GroupMeWebhookEvent[]);
    setGroupMeOnlyEvents((groupMeOnlyEventResult.data || []) as GroupMeOnlyEvent[]);
    setGroupMeOnlyParticipants((groupMeOnlyParticipantResult.data || []) as GroupMeOnlyParticipant[]);
    setStructuredSyncRows((structuredSyncResult.data || []) as StructuredGroupMeSyncRow[]);
    setMappedEvents([
      ...((matchesResult.data || []) as Array<Record<string, string | null>>).map((match) => ({
        id: match.id || '',
        kind: 'match' as const,
        team_id: match.team_id || '',
        label: `Match vs ${match.opponent_name || 'TBD'}`,
        date: match.match_date || '',
        groupme_event_id: match.groupme_event_id,
        groupme_event_title: match.groupme_event_title,
      })),
      ...((teamEventsResult.data || []) as Array<Record<string, string | null>>).map((event) => ({
        id: event.id || '',
        kind: 'event' as const,
        team_id: event.team_id || '',
        label: event.title || 'Untitled Event',
        date: event.event_date || '',
        groupme_event_id: event.groupme_event_id,
        groupme_event_title: event.groupme_event_title,
      })),
    ]);
    setSelectedTeamId((current) => current || nextTeams[0]?.id || '');
  }, [isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedTeam = teams.find((team) => team.id === selectedTeamId) || null;
  const rosterPlayerIds = useMemo(
    () => new Set(rosters.filter((roster) => roster.team_id === selectedTeamId).map((roster) => roster.player_id)),
    [rosters, selectedTeamId],
  );
  const rosterPlayers = useMemo(
    () => players.filter((player) => rosterPlayerIds.has(player.id)),
    [players, rosterPlayerIds],
  );
  const selectedEvents = useMemo(
    () => mappedEvents.filter((event) => event.team_id === selectedTeamId),
    [mappedEvents, selectedTeamId],
  );
  const duplicateNames = useMemo(() => {
    const counts = new Map<string, number>();
    rosterPlayers.forEach((player) => {
      const normalized = normalizeName(player.groupme_display_name);
      if (normalized) counts.set(normalized, (counts.get(normalized) || 0) + 1);
    });
    return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([name]) => name));
  }, [rosterPlayers]);
  const reviewEvents = events.filter((event) => event.sync_status === 'needs_review' || event.sync_status === 'error');
  const groupMeOnlyNeedingReview = groupMeOnlyParticipants.filter((participant) => participant.match_status !== 'matched').length;
  const selectedStructuredRows = structuredSyncRows.filter((row) => row.team_id === selectedTeamId);
  const diagnosticEvents = useMemo(
    () => selectedEvents.filter((event) => event.groupme_event_id),
    [selectedEvents],
  );

  useEffect(() => {
    if (!diagnosticEvents.some((event) => event.groupme_event_id === diagnosticEventId)) {
      setDiagnosticEventId(diagnosticEvents[0]?.groupme_event_id || '');
      setDiagnosticPayload(null);
      setDiagnosticSnapshots([]);
      setFirstSnapshotId('');
      setSecondSnapshotId('');
    }
  }, [diagnosticEventId, diagnosticEvents]);

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setSnack({ severity: 'success', message: 'Webhook URL copied.' });
    } catch {
      setSnack({ severity: 'error', message: 'The browser could not copy the URL.' });
    }
  };

  const authHeaders = async (json = false): Promise<Record<string, string>> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    if (json) headers['Content-Type'] = 'application/json';
    return headers;
  };

  const runEventDiagnostics = async (action: 'fetch' | 'capture' | 'list' | 'compare') => {
    if (!selectedTeamId || !diagnosticEventId) {
      setSnack({ severity: 'warning', message: 'Choose a team and linked GroupMe event first.' });
      return;
    }
    if (action === 'compare' && (!firstSnapshotId || !secondSnapshotId)) {
      setSnack({ severity: 'warning', message: 'Choose two snapshots to compare.' });
      return;
    }
    setBusyKey(`event-diagnostics:${action}`);
    try {
      const response = await fetch('/api/groupme/integration/event-diagnostics', {
        method: 'POST',
        headers: await authHeaders(true),
        body: JSON.stringify({
          action,
          teamId: selectedTeamId,
          groupmeEventId: diagnosticEventId,
          firstSnapshotId,
          secondSnapshotId,
        }),
      });
      const result = await response.json() as GroupMeEventDiagnosticPayload;
      if (!response.ok) throw new Error(result.error || 'GroupMe event diagnostics failed.');
      setDiagnosticPayload(result);
      if (result.snapshots) setDiagnosticSnapshots(result.snapshots);
      if (result.snapshot) {
        setFirstSnapshotId((current) => current || result.snapshot?.id || '');
        setSecondSnapshotId(result.snapshot.id);
      }
      setSnack({ severity: 'success', message: action === 'capture' ? 'GroupMe event snapshot captured.' : action === 'compare' ? 'Snapshots compared.' : 'GroupMe event payload loaded.' });
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to run GroupMe event diagnostics.' });
    } finally {
      setBusyKey(null);
    }
  };

  const jsonBlock = (value: unknown) => (
    <Box
      component="pre"
      sx={{
        bgcolor: '#0f1720',
        color: '#f5f7fa',
        borderRadius: 1,
        p: 1.5,
        maxHeight: 360,
        overflow: 'auto',
        fontSize: '0.78rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {JSON.stringify(value || null, null, 2)}
    </Box>
  );

  const memberDisplayNameByUserId = useMemo(
    () => new Map(groupMeMembers.map((member) => [member.userId, member.displayName])),
    [groupMeMembers],
  );
  const selectedTeamMappedPlayerIds = useMemo(
    () => new Set(groupMeMembers.map((member) => member.mappedPlayerId).filter(Boolean) as string[]),
    [groupMeMembers],
  );
  const selectedTeamMissingDurablePlayers = useMemo(
    () => rosterPlayers.filter((player) => !selectedTeamMappedPlayerIds.has(player.id)),
    [rosterPlayers, selectedTeamMappedPlayerIds],
  );
  const selectedTeamDraftReadyPlayers = useMemo(
    () => selectedTeamMissingDurablePlayers.filter((player) => Boolean(playerMemberSelections[player.id])),
    [playerMemberSelections, selectedTeamMissingDurablePlayers],
  );
  const selectedTeamMappingHealth = useMemo(() => {
    const total = rosterPlayers.length;
    const mapped = rosterPlayers.filter((player) => selectedTeamMappedPlayerIds.has(player.id)).length;
    return {
      total,
      mapped,
      missing: Math.max(0, total - mapped),
      percent: total ? Math.round((mapped / total) * 100) : 100,
    };
  }, [rosterPlayers, selectedTeamMappedPlayerIds]);
  const selectedGroupMeMemberSelectionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    Object.values(playerMemberSelections).forEach((userId) => {
      if (userId) counts.set(userId, (counts.get(userId) || 0) + 1);
    });
    return counts;
  }, [playerMemberSelections]);

  const loadGroupMeMembers = useCallback(async () => {
    if (!isAdmin || !selectedTeamId) {
      setGroupMeMembers([]);
      setGroupMeMembersDiagnostic(null);
      return;
    }
    setGroupMeMembersLoading(true);
    try {
      const response = await fetch(`/api/groupme/integration/members?teamId=${encodeURIComponent(selectedTeamId)}`, {
        headers: await authHeaders(),
      });
      const result = await response.json() as { members?: GroupMeMemberOption[]; diagnostic?: string | null; error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to load GroupMe members.');
      setGroupMeMembers(result.members || []);
      setGroupMeMembersDiagnostic(result.diagnostic || null);
    } catch (error) {
      setGroupMeMembers([]);
      setGroupMeMembersDiagnostic(error instanceof Error ? error.message : 'Unable to load GroupMe members.');
    } finally {
      setGroupMeMembersLoading(false);
    }
  }, [isAdmin, selectedTeamId]);

  useEffect(() => {
    void loadGroupMeMembers();
  }, [loadGroupMeMembers]);

  useEffect(() => {
    const memberByNormalizedName = new Map<string, GroupMeMemberOption>();
    groupMeMembers.forEach((member) => {
      [member.displayName, member.name, member.nickname]
        .map(normalizeName)
        .filter(Boolean)
        .forEach((name) => {
          if (!memberByNormalizedName.has(name)) memberByNormalizedName.set(name, member);
        });
    });
    setPlayerMemberSelections(Object.fromEntries(rosterPlayers.map((player) => {
      const mappedMember = groupMeMembers.find((member) => member.mappedPlayerId === player.id)
        || memberByNormalizedName.get(normalizeName(player.groupme_display_name));
      return [player.id, mappedMember?.userId || ''];
    })));
  }, [groupMeMembers, rosterPlayers]);

  const saveUserGroupMeToken = async () => {
    if (!userTokenDraft.trim()) {
      setSnack({ severity: 'warning', message: 'Paste your GroupMe access token before saving.' });
      return;
    }
    setBusyKey('groupme-user-token');
    try {
      const response = await fetch('/api/groupme/integration/user-token', {
        method: 'POST',
        headers: await authHeaders(true),
        body: JSON.stringify({ accessToken: userTokenDraft }),
      });
      const result = await response.json() as UserGroupMeTokenStatus & { error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to save GroupMe token.');
      setUserTokenStatus(result);
      setUserTokenDraft('');
      setSnack({ severity: 'success', message: 'GroupMe access token saved for your user.' });
      await load();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to save GroupMe token.' });
    } finally {
      setBusyKey(null);
    }
  };

  const testUserGroupMeToken = async () => {
    setBusyKey('groupme-user-test');
    try {
      const response = await fetch('/api/groupme/integration/user-test', {
        method: 'POST',
        headers: await authHeaders(true),
      });
      const result = await response.json() as { success?: boolean; message?: string; error?: string };
      if (!response.ok || result.success === false) throw new Error(result.error || result.message || 'Unable to test GroupMe token.');
      setSnack({ severity: 'success', message: result.message || 'GroupMe token test completed.' });
      await load();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to test GroupMe token.' });
    } finally {
      setBusyKey(null);
    }
  };

  const saveAdminSettings = async () => {
    setBusyKey('groupme-admin-settings');
    try {
      const classificationRules = classificationDrafts
        .map((rule) => ({
          matchType: rule.matchType,
          classification: rule.classification,
          cues: rule.cuesText.split(',').map((cue) => cue.trim()).filter(Boolean),
        }))
        .filter((rule) => rule.cues.length > 0);
      const response = await fetch('/api/groupme/integration/admin-settings', {
        method: 'POST',
        headers: await authHeaders(true),
        body: JSON.stringify({
          structuredApiEnabled: adminSettings.structuredApiEnabled,
          webhookApplyEnabled: adminSettings.webhookApplyEnabled,
          classificationRules,
        }),
      });
      const result = await response.json() as GroupMeAdminSettings & { error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to save GroupMe sync settings.');
      setAdminSettings(result);
      setClassificationDrafts((result.classificationRules || defaultClassificationRules).map((rule) => ({ ...rule, cuesText: rule.cues.join(', ') })));
      setSnack({ severity: 'success', message: 'GroupMe RSVP Sync settings saved.' });
      await load();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to save GroupMe sync settings.' });
    } finally {
      setBusyKey(null);
    }
  };

  const savePlayerMappingRequest = async (player: PlayerRow, groupmeUserId: string | null) => {
      const response = await fetch('/api/groupme/integration/member-mapping', {
        method: 'POST',
        headers: await authHeaders(true),
        body: JSON.stringify({
          teamId: selectedTeamId,
          playerId: player.id,
          groupmeUserId,
        }),
      });
      const result = await response.json() as { groupmeDisplayName?: string | null; error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to save GroupMe roster mapping.');
      return result;
  };

  const savePlayerMapping = async (player: PlayerRow) => {
    setBusyKey(`player:${player.id}`);
    try {
      const result = await savePlayerMappingRequest(player, playerMemberSelections[player.id] || null);
      setSnack({ severity: 'success', message: result.groupmeDisplayName ? 'GroupMe roster mapping saved.' : 'GroupMe roster mapping cleared.' });
      await load();
      await loadGroupMeMembers();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to save GroupMe roster mapping.' });
    } finally {
      setBusyKey(null);
    }
  };

  const autoSelectObviousGroupMeMappings = () => {
    const membersByNormalizedName = new Map<string, GroupMeMemberOption[]>();
    groupMeMembers.forEach((member) => {
      [member.displayName, member.name, member.nickname]
        .map(normalizeName)
        .filter(Boolean)
        .forEach((name) => {
          membersByNormalizedName.set(name, [...(membersByNormalizedName.get(name) || []), member]);
        });
    });
    let selectedCount = 0;
    setPlayerMemberSelections((current) => {
      const next = { ...current };
      selectedTeamMissingDurablePlayers.forEach((player) => {
        if (next[player.id]) return;
        const candidateNames = [
          `${player.first_name || ''} ${player.last_name || ''}`,
          player.groupme_display_name,
        ].map(normalizeName).filter(Boolean);
        for (const name of candidateNames) {
          const candidates = (membersByNormalizedName.get(name) || []).filter((member) => !member.mappedPlayerId || member.mappedPlayerId === player.id);
          if (candidates.length === 1) {
            next[player.id] = candidates[0].userId;
            selectedCount += 1;
            break;
          }
        }
      });
      return next;
    });
    setSnack({
      severity: selectedCount ? 'success' : 'warning',
      message: selectedCount ? `${selectedCount} obvious GroupMe mapping${selectedCount === 1 ? '' : 's'} selected. Review and save to persist.` : 'No obvious unmapped GroupMe members were found.',
    });
  };

  const saveReadyGroupMeMappings = async () => {
    if (!selectedTeamDraftReadyPlayers.length) {
      setSnack({ severity: 'warning', message: 'No ready GroupMe mappings to save.' });
      return;
    }
    const duplicateDrafts = selectedTeamDraftReadyPlayers.filter((player) => {
      const userId = playerMemberSelections[player.id];
      return userId && (selectedGroupMeMemberSelectionCounts.get(userId) || 0) > 1;
    });
    if (duplicateDrafts.length) {
      setSnack({ severity: 'error', message: 'One GroupMe member is selected for multiple players. Fix duplicates before saving.' });
      return;
    }
    setBusyKey('save-ready-groupme-mappings');
    try {
      for (const player of selectedTeamDraftReadyPlayers) {
        await savePlayerMappingRequest(player, playerMemberSelections[player.id] || null);
      }
      setSnack({ severity: 'success', message: `${selectedTeamDraftReadyPlayers.length} durable GroupMe mapping${selectedTeamDraftReadyPlayers.length === 1 ? '' : 's'} saved.` });
      await load();
      await loadGroupMeMembers();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to save ready GroupMe mappings.' });
    } finally {
      setBusyKey(null);
    }
  };

  const saveEventMapping = async (event: MappedEvent) => {
    setBusyKey(`${event.kind}:${event.id}`);
    const table = event.kind === 'match' ? 'match_events' : 'events';
    const { error } = await supabase.from(table).update({
      groupme_event_id: event.groupme_event_id?.trim() || null,
      groupme_event_title: event.groupme_event_title?.trim() || null,
    }).eq('id', event.id);
    setBusyKey(null);
    setSnack(error
      ? { severity: 'error', message: error.message }
      : { severity: 'success', message: 'GroupMe event mapping saved.' });
  };

  const sendTestPayload = async () => {
    setBusyKey('test');
    try {
      const response = await fetch('/api/groupme/webhook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: `courtcaptain-test-${Date.now()}`,
          group_id: selectedTeam?.groupme_group_id || 'courtcaptain-spike-test',
          sender_id: 'system',
          name: 'GroupMe',
          text: "Matt Ludwick is going to 'CourtCaptain Test Event'",
          system: true,
          attachments: [{ type: 'event', event_id: 'courtcaptain-test-event' }],
          courtcaptain_test: true,
        }),
      });
      const result = await response.json() as { logged?: boolean; syncStatus?: string; error?: string };
      if (!result.logged) throw new Error(result.error || 'Test payload was not logged.');
      setSnack({ severity: 'success', message: `Test event logged with status: ${result.syncStatus || 'logged'}.` });
      await load();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to send test payload.' });
    } finally {
      setBusyKey(null);
    }
  };

  const runStructuredSync = async () => {
    if (!selectedTeamId) return;
    setBusyKey('structured-sync');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Sign in again before running structured GroupMe sync.');
      const response = await fetch('/api/groupme/structured-sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ teamId: selectedTeamId, limit: 20 }),
      });
      const result = await response.json() as { synced?: number; diagnostic?: string | null; error?: string };
      if (!response.ok) throw new Error(result.error || 'Structured GroupMe sync failed.');
      const syncedCount = result.synced || 0;
      setSnack({
        severity: syncedCount === 0 || result.diagnostic ? 'warning' : 'success',
        message: [
          `Structured GroupMe sync completed for ${syncedCount} event${syncedCount === 1 ? '' : 's'}.`,
          result.diagnostic || '',
          syncedCount === 0 ? 'GroupMe returned no events for this group after the requested start time.' : '',
        ].filter(Boolean).join(' '),
      });
      await load();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to run structured GroupMe sync.' });
    } finally {
      setBusyKey(null);
    }
  };

  const resolveReview = async (event: GroupMeWebhookEvent, apply: boolean) => {
    const playerId = reviewPlayerSelections[event.id] || event.matched_player_id || null;
    const eventSelection = reviewEventSelections[event.id]
      || (event.matched_event_id && event.matched_event_kind ? `${event.matched_event_kind}:${event.matched_event_id}` : '');
    const [eventKind, eventId] = eventSelection ? eventSelection.split(':') : [null, null];
    if (apply && !playerId) {
      setSnack({ severity: 'warning', message: 'Choose a roster player before applying this RSVP.' });
      return;
    }
    if (apply && !eventId) {
      setSnack({ severity: 'warning', message: 'Choose the CourtCaptain match or event before applying this RSVP.' });
      return;
    }
    setBusyKey(`review:${event.id}`);
    const { error } = await supabase.rpc('resolve_groupme_rsvp_review', {
      p_webhook_event_id: event.id,
      p_player_id: playerId,
      p_courtcaptain_event_id: eventId,
      p_courtcaptain_event_kind: eventKind,
      p_apply: apply,
    });
    setBusyKey(null);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: apply ? 'RSVP applied from the review queue.' : 'Webhook event ignored.' });
    await load();
  };

  const resolveGroupMeOnlyParticipant = async (participant: GroupMeOnlyParticipant) => {
    const playerId = participantPlayerSelections[participant.id];
    if (!playerId) {
      setSnack({ severity: 'warning', message: 'Choose a player before saving this GroupMe identity mapping.' });
      return;
    }
    setBusyKey(`groupme-only:${participant.id}`);
    const { error } = await supabase.rpc('resolve_groupme_only_rsvp_participant', {
      p_participant_id: participant.id,
      p_player_id: playerId,
    });
    setBusyKey(null);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'GroupMe participant mapped to player for future RSVP summaries.' });
    await load();
  };

  if (!isAdmin) return <Alert severity="warning">GroupMe RSVP Sync is available only to administrators.</Alert>;

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1180 }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ScienceIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>GroupMe RSVP Sync</Typography>
          <Chip label="Experimental" size="small" color="warning" variant="outlined" />
          <Chip
            label={adminSettings.structuredApiEnabled ? 'API Sync Enabled' : 'API Sync Disabled'}
            size="small"
            color={adminSettings.structuredApiEnabled ? 'success' : 'default'}
          />
          <Chip
            label={adminSettings.webhookApplyEnabled ? 'Webhook Applies RSVPs' : 'Webhook Log Only'}
            size="small"
            color={adminSettings.webhookApplyEnabled ? 'warning' : 'default'}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          Deterministically map native GroupMe Event responses into CourtCaptain availability.
        </Typography>
      </Box>

      {!featureStatus.rsvpSyncConfigured && (
        <Alert severity="warning">
          Automatic sync is not server-configured. Add the server-only Supabase service-role secret before enabling the flag.
        </Alert>
      )}
      <Alert severity={adminSettings.webhookApplyEnabled ? 'warning' : 'info'}>
        Webhook callbacks can remain configured for diagnostics. Keep webhook application off when structured API sync should be authoritative.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Staging webhook</Typography>
            <Typography component="code" sx={{ display: 'block', overflowWrap: 'anywhere', fontSize: '0.82rem' }}>{stagingWebhookUrl}</Typography>
          </Box>
          <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => void copyUrl(stagingWebhookUrl)}>Copy</Button>
          <Button variant="outlined" startIcon={<SendIcon />} disabled={busyKey === 'test'} onClick={() => void sendTestPayload()}>Send Test</Button>
          <Button startIcon={<RefreshIcon />} disabled={loading} onClick={() => void load()}>Refresh</Button>
        </Stack>
      </Paper>

      <Tabs value={pageTab} onChange={(_event, value: PageTab) => setPageTab(value)} variant="scrollable" scrollButtons="auto">
        <Tab value="configuration" label="Configuration" />
        <Tab value="setup" label="Team & Players" />
        <Tab value="mappings" label="Event Mappings" />
        <Tab value="structured" label={`Structured Sync (${structuredSyncRows.length})`} />
        <Tab value="diagnostics" label="Event API Diagnostics" />
        <Tab value="review" label={`Needs Review (${reviewEvents.length})`} />
        <Tab value="groupme-only" label={`GroupMe-Only Events (${groupMeOnlyEvents.length}${groupMeOnlyNeedingReview ? ` · ${groupMeOnlyNeedingReview} unmatched` : ''})`} />
        <Tab value="events" label={`Recent Events (${events.length})`} />
      </Tabs>

      {loading ? (
        <Paper variant="outlined" sx={{ py: 6, display: 'grid', placeItems: 'center' }}><CircularProgress /></Paper>
      ) : (
        <>
          {pageTab === 'configuration' && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>My GroupMe Access Token</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Stored once for your CourtCaptain user and reused by every team where GroupMe RSVP Sync is enabled.
                      </Typography>
                    </Box>
                    <Chip size="small" label={userTokenStatus?.tokenNeedsResave ? 'User token needs re-save' : userTokenStatus?.tokenConfigured ? 'User token configured' : 'User token not configured'} color={userTokenStatus?.tokenNeedsResave ? 'warning' : userTokenStatus?.tokenConfigured ? 'success' : 'default'} />
                  </Stack>
                  {userTokenStatus?.serverConfigured === false && (
                    <Alert severity="warning">GroupMe token encryption is missing: {userTokenStatus.missingConfigKeys?.join(', ')}.</Alert>
                  )}
                  {userTokenStatus?.tokenNeedsResave && (
                    <Alert severity="warning">Your saved GroupMe access token cannot be decrypted. Re-enter it below, select Save Token, then select Test Token before sending a pinned schedule.</Alert>
                  )}
                  {userTokenStatus?.lastError && <Alert severity="warning">{userTokenStatus.lastError}</Alert>}
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                      label="My GroupMe Access Token"
                      type="password"
                      value={userTokenDraft}
                      onChange={(event) => setUserTokenDraft(event.target.value)}
                      helperText={userTokenStatus?.tokenConfigured ? 'Leave blank to keep your saved token.' : 'Create this in GroupMe and paste it here once.'}
                      fullWidth
                    />
                    <Button variant="outlined" startIcon={<SaveIcon />} disabled={busyKey === 'groupme-user-token'} onClick={() => void saveUserGroupMeToken()}>Save Token</Button>
                    <Button variant="contained" disabled={busyKey === 'groupme-user-test' || (!userTokenStatus?.tokenConfigured && !userTokenStatus?.fallbackAvailable)} onClick={() => void testUserGroupMeToken()}>Test Token</Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Last tested: {userTokenStatus?.lastTestedAt ? formattedTimestamp(userTokenStatus.lastTestedAt) : '—'} · Last success: {userTokenStatus?.lastSuccessAt ? formattedTimestamp(userTokenStatus.lastSuccessAt) : '—'}
                  </Typography>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>RSVP Update Method</Typography>
                  <Alert severity={adminSettings.webhookApplyEnabled ? 'warning' : 'success'}>
                    {adminSettings.webhookApplyEnabled
                      ? 'Old webhook/bot RSVP messages can still apply availability updates. Turn this off when structured API sync should be authoritative.'
                      : 'Webhook/bot callbacks are log-only. RSVP updates are expected to come from the structured GroupMe API sync.'}
                  </Alert>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControlLabel
                      control={<Checkbox checked={adminSettings.structuredApiEnabled} onChange={(event) => setAdminSettings((current) => ({ ...current, structuredApiEnabled: event.target.checked }))} />}
                      label="Use structured GroupMe API for RSVP updates"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={adminSettings.webhookApplyEnabled} onChange={(event) => setAdminSettings((current) => ({ ...current, webhookApplyEnabled: event.target.checked }))} />}
                      label="Allow webhook/bot messages to apply RSVP updates"
                    />
                  </Stack>
                  {featureStatus.webhookEnvEnabled && !adminSettings.webhookApplyEnabled && (
                    <Alert severity="info">The Cloudflare environment flag for webhook sync is on, but CourtCaptain settings keep webhook callbacks in log-only mode.</Alert>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Event Title Classification</Typography>
                    <Typography variant="body2" color="text.secondary">
                      These cues classify newly discovered GroupMe events before CourtCaptain creates or updates the linked event.
                    </Typography>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Rule</TableCell>
                        <TableCell>Cues</TableCell>
                        <TableCell>Classify As</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classificationDrafts.map((rule, index) => (
                        <TableRow key={`${rule.matchType}:${index}`}>
                          <TableCell sx={{ width: 180 }}>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={rule.matchType}
                                onChange={(event) => setClassificationDrafts((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, matchType: event.target.value as ClassificationRule['matchType'] } : row))}
                              >
                                <MenuItem value="begins_with">Begins with</MenuItem>
                                <MenuItem value="contains">Contains</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={rule.cuesText}
                              onChange={(event) => setClassificationDrafts((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, cuesText: event.target.value } : row))}
                              helperText="Comma-separated"
                            />
                          </TableCell>
                          <TableCell sx={{ width: 220 }}>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={rule.classification}
                                onChange={(event) => setClassificationDrafts((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, classification: event.target.value as ClassificationRule['classification'] } : row))}
                              >
                                <MenuItem value="Match">Match</MenuItem>
                                <MenuItem value="Practice">Practice</MenuItem>
                                <MenuItem value="Social Event">Social Event</MenuItem>
                                <MenuItem value="Meeting">Meeting</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined" onClick={() => setClassificationDrafts((current) => [...current, { matchType: 'contains', cues: [], cuesText: '', classification: 'Other' }])}>Add Rule</Button>
                    <Button variant="contained" startIcon={<SaveIcon />} disabled={busyKey === 'groupme-admin-settings'} onClick={() => void saveAdminSettings()}>Save Configuration</Button>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          )}

          {(pageTab === 'setup' || pageTab === 'mappings') && (
            <FormControl fullWidth>
              <InputLabel>Team</InputLabel>
              <Select label="Team" value={selectedTeamId} onChange={(event) => setSelectedTeamId(event.target.value)}>
                {teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {pageTab === 'setup' && selectedTeam && (
            <Stack spacing={2}>
              <Alert severity="info">
                Team-to-GroupMe group selection and the per-team RSVP sync checkbox are managed in Teams. This page keeps user token, sync method, title rules, and player identity review in one place.
              </Alert>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Roster Player Setup</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the GroupMe member that belongs to each CourtCaptain roster player.
                </Typography>
                {!selectedTeam.groupme_group_id && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Select a GroupMe group for this team in Teams before mapping roster players.
                  </Alert>
                )}
                {groupMeMembersDiagnostic && (
                  <Alert severity={groupMeMembers.length ? 'info' : 'warning'} sx={{ mb: 2 }}>{groupMeMembersDiagnostic}</Alert>
                )}
                {selectedTeam.groupme_group_id && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: selectedTeamMappingHealth.missing ? 'warning.50' : 'success.50' }}>
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Durable GroupMe ID Health</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedTeamMappingHealth.mapped} of {selectedTeamMappingHealth.total} roster players have durable GroupMe user IDs for this group.
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip color={selectedTeamMappingHealth.missing ? 'warning' : 'success'} label={`${selectedTeamMappingHealth.percent}% mapped`} />
                          <Chip variant="outlined" label={`${selectedTeamMappingHealth.missing} missing`} />
                          <Chip variant="outlined" label={`${selectedTeamDraftReadyPlayers.length} ready to save`} />
                        </Stack>
                      </Stack>
                      {selectedTeamMappingHealth.missing > 0 && (
                        <Alert severity="warning">
                          RSVP sync is most reliable when every roster player is mapped to a real GroupMe member. Display-name fallbacks can help, but durable GroupMe user IDs are safer.
                        </Alert>
                      )}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                          disabled={groupMeMembersLoading || !groupMeMembers.length || selectedTeamMappingHealth.missing === 0}
                          onClick={autoSelectObviousGroupMeMappings}
                        >
                          Auto-select Obvious Matches
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={busyKey === 'save-ready-groupme-mappings' || groupMeMembersLoading || selectedTeamDraftReadyPlayers.length === 0}
                          onClick={() => void saveReadyGroupMeMappings()}
                        >
                          Save Ready Mappings
                        </Button>
                      </Stack>
                      {selectedTeamMissingDurablePlayers.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Missing durable IDs: {selectedTeamMissingDurablePlayers.map((player) => `${player.first_name} ${player.last_name}`).join(', ')}
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                )}
                <Stack divider={<Divider flexItem />} spacing={1.5}>
                  {rosterPlayers.map((player) => {
                    const selectedMemberUserId = playerMemberSelections[player.id] || '';
                    const selectedDisplayName = selectedMemberUserId ? memberDisplayNameByUserId.get(selectedMemberUserId) || player.groupme_display_name : '';
                    const normalized = normalizeName(selectedDisplayName);
                    const duplicate = normalized ? duplicateNames.has(normalized) : false;
                    const hasDurableMapping = selectedTeamMappedPlayerIds.has(player.id);
                    const duplicateDraftSelection = selectedMemberUserId ? (selectedGroupMeMemberSelectionCounts.get(selectedMemberUserId) || 0) > 1 : false;
                    return (
                      <Stack key={player.id} direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                        <Typography sx={{ minWidth: 190, fontWeight: 600 }}>{player.first_name} {player.last_name}</Typography>
                        <FormControl size="small" fullWidth disabled={!selectedTeam.groupme_group_id || groupMeMembersLoading}>
                          <InputLabel>GroupMe User</InputLabel>
                          <Select
                            label="GroupMe User"
                            value={selectedMemberUserId}
                            onChange={(event) => {
                              const userId = String(event.target.value);
                              const member = groupMeMembers.find((row) => row.userId === userId) || null;
                              setPlayerMemberSelections((current) => ({ ...current, [player.id]: userId }));
                              setPlayers((current) => current.map((row) => row.id === player.id ? { ...row, groupme_display_name: member?.displayName || null } : row));
                            }}
                          >
                            <MenuItem value=""><em>Not mapped</em></MenuItem>
                            {groupMeMembers.map((member) => {
                              const mappedElsewhere = Boolean(member.mappedPlayerId && member.mappedPlayerId !== player.id);
                              return (
                                <MenuItem key={member.userId} value={member.userId} disabled={mappedElsewhere}>
                                  {member.displayName}{mappedElsewhere && member.mappedPlayerName ? ` (mapped to ${member.mappedPlayerName})` : ''}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                        <Chip
                          size="small"
                          label={groupMeMembersLoading ? 'Loading' : hasDurableMapping ? 'Durable ID saved' : duplicateDraftSelection || duplicate ? 'Duplicate' : selectedMemberUserId ? 'Ready to save' : 'Missing durable ID'}
                          color={hasDurableMapping ? 'success' : duplicateDraftSelection || duplicate ? 'error' : selectedMemberUserId ? 'info' : 'warning'}
                        />
                        <Button
                          variant="outlined"
                          disabled={busyKey === `player:${player.id}` || groupMeMembersLoading || !selectedTeam.groupme_group_id}
                          onClick={() => void savePlayerMapping(player)}
                        >
                          Save
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
            </Stack>
          )}
          {pageTab === 'structured' && (
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Team</InputLabel>
                <Select label="Team" value={selectedTeamId} onChange={(event) => setSelectedTeamId(event.target.value)}>
                  {teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}</MenuItem>)}
                </Select>
              </FormControl>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Structured GroupMe Event Sync</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pulls GroupMe calendar events by event ID, uses structured time/location/RSVP arrays, and records mapping diagnostics.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    disabled={!selectedTeam?.groupme_group_id || busyKey === 'structured-sync'}
                    onClick={() => void runStructuredSync()}
                  >
                    Sync Group Events
                  </Button>
                </Stack>
                {!selectedTeam?.groupme_group_id && <Alert severity="warning" sx={{ mt: 2 }}>Choose a team with a GroupMe group ID before running structured sync.</Alert>}
              </Paper>
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>GroupMe Event</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>RSVP Counts</TableCell>
                      <TableCell>Mapping</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Synced</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedStructuredRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{row.raw_name || row.groupme_event_id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.raw_start_at ? formattedTimestamp(row.raw_start_at) : 'Start not set'} · {row.groupme_event_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.court_captain_event_type || row.classification_status} />
                          {row.parsed_opponent_name && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {row.parsed_opponent_name} · {row.parsed_match_site_type || 'site unknown'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.going_count} going · {row.maybe_count || 0} maybe · {row.not_going_count} not going</Typography>
                          <Typography variant="caption" color="text.secondary">{row.no_response_count} no response</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.mapped_count} mapped</Typography>
                          <Typography variant="caption" color={row.ambiguous_count || row.unmatched_count ? 'warning.main' : 'text.secondary'}>
                            {row.ambiguous_count} ambiguous · {row.unmatched_count} unmatched
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.sync_status}
                            color={row.sync_status === 'synced' ? 'success' : row.sync_status === 'needs_review' ? 'warning' : row.sync_status === 'error' ? 'error' : 'default'}
                          />
                          {row.sync_error && <Typography variant="caption" display="block" color="warning.main">{row.sync_error}</Typography>}
                        </TableCell>
                        <TableCell>{row.last_synced_at ? formattedTimestamp(row.last_synced_at) : '—'}</TableCell>
                      </TableRow>
                    ))}
                    {selectedStructuredRows.length === 0 && (
                      <TableRow><TableCell colSpan={6}>No structured GroupMe sync runs have been recorded for this team.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Stack>
          )}

          {pageTab === 'diagnostics' && (
            <Stack spacing={2}>
              <Alert severity="warning">
                Staging Diagnostic — Not for Production Use. Use this to capture raw GroupMe event payloads before and after RSVP or event-detail changes.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>GroupMe Event API Diagnostics</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <FormControl fullWidth>
                      <InputLabel>Team</InputLabel>
                      <Select label="Team" value={selectedTeamId} onChange={(event) => setSelectedTeamId(event.target.value)}>
                        {teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth disabled={!selectedTeam?.groupme_group_id || diagnosticEvents.length === 0}>
                      <InputLabel>Linked GroupMe Event</InputLabel>
                      <Select label="Linked GroupMe Event" value={diagnosticEventId} onChange={(event) => setDiagnosticEventId(event.target.value)}>
                        {diagnosticEvents.map((event) => (
                          <MenuItem key={`${event.kind}:${event.id}`} value={event.groupme_event_id || ''}>
                            {event.date} · {event.label} · {event.groupme_event_id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  {!selectedTeam?.groupme_group_id && <Alert severity="warning">Choose a team mapped to a GroupMe group first.</Alert>}
                  {selectedTeam?.groupme_group_id && diagnosticEvents.length === 0 && (
                    <Alert severity="info">No linked GroupMe events were found for this team. Link or publish an event first.</Alert>
                  )}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} disabled={busyKey?.startsWith('event-diagnostics') || !diagnosticEventId} onClick={() => void runEventDiagnostics('fetch')}>
                      Fetch Current Payload
                    </Button>
                    <Button variant="contained" startIcon={<SaveIcon />} disabled={busyKey?.startsWith('event-diagnostics') || !diagnosticEventId} onClick={() => void runEventDiagnostics('capture')}>
                      Capture Snapshot
                    </Button>
                    <Button disabled={busyKey?.startsWith('event-diagnostics') || !diagnosticEventId} onClick={() => void runEventDiagnostics('list')}>
                      Refresh Snapshots
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {diagnosticPayload?.normalizedSubset && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Current Payload</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Event hash: {diagnosticPayload.eventContentHash} · RSVP hash: {diagnosticPayload.rsvpHash}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography variant="subtitle2">Normalized Subset</Typography>
                    {jsonBlock(diagnosticPayload.normalizedSubset)}
                    <Typography variant="subtitle2">Raw JSON Response</Typography>
                    {jsonBlock(diagnosticPayload.rawPayload)}
                  </Stack>
                </Paper>
              )}

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Snapshot History</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Captured</TableCell>
                        <TableCell>Event Hash</TableCell>
                        <TableCell>RSVP Hash</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {diagnosticSnapshots.map((snapshot) => (
                        <TableRow key={snapshot.id}>
                          <TableCell>{formattedTimestamp(snapshot.captured_at)}</TableCell>
                          <TableCell><Typography component="code" sx={{ fontSize: '0.72rem', wordBreak: 'break-all' }}>{snapshot.event_content_hash}</Typography></TableCell>
                          <TableCell><Typography component="code" sx={{ fontSize: '0.72rem', wordBreak: 'break-all' }}>{snapshot.rsvp_hash}</Typography></TableCell>
                          <TableCell>{snapshot.notes || '—'}</TableCell>
                        </TableRow>
                      ))}
                      {diagnosticSnapshots.length === 0 && (
                        <TableRow><TableCell colSpan={4}>No snapshots captured for this GroupMe event yet.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Snapshot A</InputLabel>
                      <Select label="Snapshot A" value={firstSnapshotId} onChange={(event) => setFirstSnapshotId(event.target.value)}>
                        {diagnosticSnapshots.map((snapshot) => <MenuItem key={snapshot.id} value={snapshot.id}>{formattedTimestamp(snapshot.captured_at)}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>Snapshot B</InputLabel>
                      <Select label="Snapshot B" value={secondSnapshotId} onChange={(event) => setSecondSnapshotId(event.target.value)}>
                        {diagnosticSnapshots.map((snapshot) => <MenuItem key={snapshot.id} value={snapshot.id}>{formattedTimestamp(snapshot.captured_at)}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" disabled={busyKey?.startsWith('event-diagnostics') || !firstSnapshotId || !secondSnapshotId} onClick={() => void runEventDiagnostics('compare')}>
                      Compare
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {diagnosticPayload?.normalizedDiff && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Structured Diff</Typography>
                    {diagnosticPayload.changed && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={diagnosticPayload.changed.eventContentHash ? 'Event definition changed' : 'Event definition unchanged'} color={diagnosticPayload.changed.eventContentHash ? 'warning' : 'success'} size="small" />
                        <Chip label={diagnosticPayload.changed.rsvpHash ? 'RSVP state changed' : 'RSVP state unchanged'} color={diagnosticPayload.changed.rsvpHash ? 'warning' : 'success'} size="small" />
                      </Stack>
                    )}
                    <Typography variant="subtitle2">Normalized Diff</Typography>
                    {jsonBlock(diagnosticPayload.normalizedDiff)}
                    <Typography variant="subtitle2">Raw Payload Diff</Typography>
                    {jsonBlock(diagnosticPayload.diff)}
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}

          {pageTab === 'mappings' && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>GroupMe Event Mappings</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Auto-sync requires the exact GroupMe attachment event ID. Event titles are informational only.
              </Typography>
              <Stack divider={<Divider flexItem />} spacing={1.5}>
                {selectedEvents.map((event) => (
                  <Stack key={`${event.kind}:${event.id}`} spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={event.kind === 'match' ? 'Match' : 'Team Event'} />
                      <Typography sx={{ fontWeight: 700 }}>{event.date} · {event.label}</Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                      <TextField
                        size="small"
                        fullWidth
                        label="GroupMe Event ID"
                        value={event.groupme_event_id || ''}
                        onChange={(change) => setMappedEvents((current) => current.map((row) => row.id === event.id && row.kind === event.kind ? { ...row, groupme_event_id: change.target.value } : row))}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="GroupMe Event Title"
                        value={event.groupme_event_title || ''}
                        onChange={(change) => setMappedEvents((current) => current.map((row) => row.id === event.id && row.kind === event.kind ? { ...row, groupme_event_title: change.target.value } : row))}
                      />
                      <Chip size="small" label={event.groupme_event_id ? 'Mapped' : 'Not Mapped'} color={event.groupme_event_id ? 'success' : 'warning'} />
                      <Button variant="outlined" disabled={busyKey === `${event.kind}:${event.id}`} onClick={() => void saveEventMapping(event)}>Save</Button>
                    </Stack>
                  </Stack>
                ))}
                {selectedEvents.length === 0 && <Alert severity="info">No matches or team events found for this team.</Alert>}
              </Stack>
            </Paper>
          )}

          {pageTab === 'review' && (
            <Stack spacing={2}>
              {reviewEvents.length === 0 && <Alert severity="success">There are no GroupMe RSVP events requiring review.</Alert>}
              {reviewEvents.map((event) => {
                const reviewTeamId = event.matched_team_id
                  || teams.find((team) => team.groupme_group_id === event.groupme_group_id)?.id
                  || null;
                const teamPlayers = players.filter((player) => rosters.some((roster) => roster.team_id === reviewTeamId && roster.player_id === player.id));
                const teamEvents = mappedEvents.filter((mappedEvent) => mappedEvent.team_id === reviewTeamId);
                const needsPlayerSelection = !event.matched_player_id;
                const needsEventSelection = !event.matched_event_id || !event.matched_event_kind;
                return (
                  <Paper key={event.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip label={event.sync_status} color={statusColor(event.sync_status)} size="small" />
                        {event.parsed_status && <Chip label={event.parsed_status} size="small" variant="outlined" />}
                        <Typography variant="caption" color="text.secondary">{formattedTimestamp(event.received_at)}</Typography>
                      </Stack>
                      <Typography sx={{ fontWeight: 700 }}>{event.text || '(No message text)'}</Typography>
                      {event.sync_error && <Alert severity={event.sync_status === 'error' ? 'error' : 'warning'}>{event.sync_error}</Alert>}
                      {needsEventSelection ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>Correct CourtCaptain Match or Event</InputLabel>
                          <Select
                            label="Correct CourtCaptain Match or Event"
                            value={reviewEventSelections[event.id] || ''}
                            onChange={(change) => setReviewEventSelections((current) => ({ ...current, [event.id]: change.target.value }))}
                          >
                            {teamEvents.map((mappedEvent) => (
                              <MenuItem key={`${mappedEvent.kind}:${mappedEvent.id}`} value={`${mappedEvent.kind}:${mappedEvent.id}`}>
                                {mappedEvent.kind === 'match' ? 'Match' : 'Team Event'} · {mappedEvent.date} · {mappedEvent.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Alert severity="success">
                          Event matched to {teamEvents.find((mappedEvent) => mappedEvent.id === event.matched_event_id && mappedEvent.kind === event.matched_event_kind)?.label || 'CourtCaptain event'}.
                        </Alert>
                      )}
                      {needsPlayerSelection ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>Correct Roster Player</InputLabel>
                          <Select
                            label="Correct Roster Player"
                            value={reviewPlayerSelections[event.id] || ''}
                            onChange={(change) => setReviewPlayerSelections((current) => ({ ...current, [event.id]: change.target.value }))}
                          >
                            {teamPlayers.map((player) => <MenuItem key={player.id} value={player.id}>{player.first_name} {player.last_name}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ) : (
                        <Alert severity="success">
                          Player matched to {teamPlayers.find((player) => player.id === event.matched_player_id)?.first_name || 'roster player'} {teamPlayers.find((player) => player.id === event.matched_player_id)?.last_name || ''}.
                        </Alert>
                      )}
                      <Stack direction="row" spacing={1}>
                        <Button variant="contained" disabled={busyKey === `review:${event.id}`} onClick={() => void resolveReview(event, true)}>Apply RSVP</Button>
                        <Button color="inherit" disabled={busyKey === `review:${event.id}`} onClick={() => void resolveReview(event, false)}>Ignore</Button>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}

          {pageTab === 'groupme-only' && (
            <Stack spacing={2}>
              <Alert severity="info">
                These are native GroupMe events that are not linked to a CourtCaptain match or team event. They are useful RSVP summaries only: they do not create availability records, affect lineups, scheduling, recommendations, participation, or reminders.
              </Alert>
              {groupMeOnlyEvents.map((event) => {
                const participants = groupMeOnlyParticipants.filter((participant) => participant.groupme_only_rsvp_event_id === event.id);
                const summary = summarizeGroupMeOnlyRsvps(participants);
                const goingGroups = groupGoingParticipants(participants);
                return (
                  <Paper key={event.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>{event.event_name}</Typography>
                            <Chip size="small" label="GroupMe-only" color="info" variant="outlined" />
                            {event.sync_status === 'needs_review' && <Chip size="small" label="Needs review" color="warning" />}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {groupMeOnlyEventDisplayDate(event.event_start_at, event.source_updated_at)} · Group {event.groupme_group_name || event.groupme_group_id}
                          </Typography>
                        </Box>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>Refresh</Button>
                      </Stack>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' }, gap: 1 }}>
                        <SummaryTile label="Going" value={summary.goingTotal} detail={`${summary.goingMen} men · ${summary.goingWomen} women`} />
                        <SummaryTile label="Unspecified matched" value={summary.goingUnspecifiedMatched} />
                        <SummaryTile label="Unmatched going" value={summary.goingUnmatched} />
                        <SummaryTile label="Not Going" value={summary.notGoingTotal} detail={`${summary.notGoingMen} men · ${summary.notGoingWomen} women`} />
                        <SummaryTile label="Maybe" value={summary.maybeTotal} />
                        <SummaryTile label="Unknown / review" value={summary.unknownTotal + summary.unmatchedTotal + summary.ambiguousTotal} />
                      </Box>

                      <GoingParticipantList groups={goingGroups} />

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>GroupMe Name</TableCell>
                            <TableCell>RSVP</TableCell>
                            <TableCell>Matched Player</TableCell>
                            <TableCell>Match Status</TableCell>
                            <TableCell>Updated</TableCell>
                            <TableCell align="right">Manual Mapping</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {participants.map((participant) => {
                            const matchedName = participant.players
                              ? `${participant.players.first_name} ${participant.players.last_name}`
                              : null;
                            return (
                              <TableRow key={participant.id}>
                                <TableCell>
                                  <Typography sx={{ fontWeight: 700 }}>{participant.groupme_display_name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{participant.groupme_user_id || 'No GroupMe user id in payload'}</Typography>
                                </TableCell>
                                <TableCell><Chip size="small" label={participant.latest_rsvp_status} /></TableCell>
                                <TableCell>{matchedName || '—'}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={`${participant.match_status} · ${participant.mapping_source}`}
                                    color={participant.match_status === 'matched' ? 'success' : participant.match_status === 'ambiguous' ? 'warning' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>{participant.source_updated_at ? formattedTimestamp(participant.source_updated_at) : '—'}</TableCell>
                                <TableCell align="right">
                                  {participant.match_status === 'matched' ? (
                                    <Typography variant="caption" color="text.secondary">Saved</Typography>
                                  ) : (
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                      <FormControl size="small" sx={{ minWidth: 220 }}>
                                        <InputLabel>Player</InputLabel>
                                        <Select
                                          label="Player"
                                          value={participantPlayerSelections[participant.id] || ''}
                                          onChange={(change) => setParticipantPlayerSelections((current) => ({ ...current, [participant.id]: change.target.value }))}
                                        >
                                          {players.map((player) => <MenuItem key={player.id} value={player.id}>{player.first_name} {player.last_name}</MenuItem>)}
                                        </Select>
                                      </FormControl>
                                      <Button
                                        variant="outlined"
                                        disabled={busyKey === `groupme-only:${participant.id}`}
                                        onClick={() => void resolveGroupMeOnlyParticipant(participant)}
                                      >
                                        Save
                                      </Button>
                                    </Stack>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {participants.length === 0 && (
                            <TableRow><TableCell colSpan={6}>No RSVP participants captured yet.</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Stack>
                  </Paper>
                );
              })}
              {groupMeOnlyEvents.length === 0 && <Alert severity="success">No GroupMe-only RSVP events have been captured yet.</Alert>}
            </Stack>
          )}

          {pageTab === 'events' && (
            <Paper variant="outlined">
              <Stack divider={<Divider flexItem />}>
                {events.map((event) => <WebhookEventCard key={event.id} event={event} players={players} mappedEvents={mappedEvents} />)}
                {events.length === 0 && <Alert severity="info" sx={{ m: 2 }}>No webhook events have been recorded.</Alert>}
              </Stack>
            </Paper>
          )}
        </>
      )}

      <Typography variant="caption" color="text.secondary">
        Production webhook remains {productionWebhookUrl}. Do not enable production sync until staging reliability is accepted.
      </Typography>

      <Snackbar open={Boolean(snack)} autoHideDuration={5500} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}

function WebhookEventCard({
  event,
  players,
  mappedEvents,
}: {
  event: GroupMeWebhookEvent;
  players: PlayerRow[];
  mappedEvents: MappedEvent[];
}) {
  const matchedPlayer = players.find((player) => player.id === event.matched_player_id);
  const matchedEvent = mappedEvents.find((item) => item.id === event.matched_event_id && item.kind === event.matched_event_kind);
  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={event.sync_status || 'logged'} size="small" color={statusColor(event.sync_status || 'logged')} />
            {event.parsed_status && <Chip label={event.parsed_status} size="small" variant="outlined" />}
            {event.source === 'courtcaptain_manual_test' && <Chip label="manual test" size="small" variant="outlined" />}
          </Stack>
          <Typography variant="caption" color="text.secondary">{formattedTimestamp(event.received_at)}</Typography>
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{event.sender_name || 'Unknown sender'}</Typography>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{event.text || '(No text)'}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
          <Detail label="Parsed player" value={event.parsed_player_name} />
          <Detail label="Matched player" value={matchedPlayer ? `${matchedPlayer.first_name} ${matchedPlayer.last_name}` : null} />
          <Detail label="Matched event" value={matchedEvent ? `${matchedEvent.date} · ${matchedEvent.label}` : null} />
        </Box>
        {event.sync_error && <Alert severity={event.sync_status === 'error' ? 'error' : 'warning'}>{event.sync_error}</Alert>}
        <Typography variant="caption" color="text.secondary">
          Group: {event.groupme_group_id || 'unknown'} · GroupMe event: {event.groupme_event_id || 'missing'} · Message: {event.groupme_message_id || 'missing'}
        </Typography>
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="body2" sx={{ fontWeight: 600 }}>Raw payload</Typography></AccordionSummary>
          <AccordionDetails sx={{ bgcolor: 'background.default' }}>
            <Box component="pre" sx={{ m: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 360, overflow: 'auto' }}>
              {JSON.stringify(event.raw_payload, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Box>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Box>
  );
}

function participantDisplayName(participant: GroupMeOnlyParticipant): string {
  if (participant.players) return `${participant.players.first_name} ${participant.players.last_name}`;
  return participant.groupme_display_name;
}

function groupGoingParticipants(participants: GroupMeOnlyParticipant[]) {
  const groups = {
    men: [] as GroupMeOnlyParticipant[],
    women: [] as GroupMeOnlyParticipant[],
    unspecified: [] as GroupMeOnlyParticipant[],
    unmatched: [] as GroupMeOnlyParticipant[],
  };

  participants
    .filter((participant) => participant.latest_rsvp_status === 'Going')
    .forEach((participant) => {
      if (participant.match_status !== 'matched' || !participant.players) {
        groups.unmatched.push(participant);
      } else if (participant.players.sex === 'male') {
        groups.men.push(participant);
      } else if (participant.players.sex === 'female') {
        groups.women.push(participant);
      } else {
        groups.unspecified.push(participant);
      }
    });

  return groups;
}

function GoingParticipantList({ groups }: { groups: ReturnType<typeof groupGoingParticipants> }) {
  const totalGoing = groups.men.length + groups.women.length + groups.unspecified.length + groups.unmatched.length;
  if (totalGoing === 0) return null;

  const hasMenAndWomen = groups.men.length > 0 && groups.women.length > 0;
  const visibleGroups = hasMenAndWomen
    ? [
      { label: 'Men Going', participants: groups.men },
      { label: 'Women Going', participants: groups.women },
      { label: 'Unspecified Going', participants: groups.unspecified },
      { label: 'Unmatched Going', participants: groups.unmatched },
    ]
    : [
      { label: 'Going', participants: [...groups.men, ...groups.women, ...groups.unspecified, ...groups.unmatched] },
    ];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>People Going</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: hasMenAndWomen ? 'repeat(2, minmax(0, 1fr))' : '1fr' }, gap: 1.25 }}>
        {visibleGroups.filter((group) => group.participants.length > 0).map((group) => (
          <Box key={group.label}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{group.label} ({group.participants.length})</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              {group.participants
                .sort((a, b) => participantDisplayName(a).localeCompare(participantDisplayName(b), undefined, { sensitivity: 'base' }))
                .map((participant) => (
                  <Chip
                    key={participant.id}
                    size="small"
                    label={participantDisplayName(participant)}
                    color={participant.match_status === 'matched' ? 'success' : 'default'}
                    variant={participant.match_status === 'matched' ? 'filled' : 'outlined'}
                  />
                ))}
            </Stack>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function SummaryTile({ label, value, detail }: { label: string; value: number; detail?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 800 }}>{value}</Typography>
      {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
    </Paper>
  );
}
